import React, { useState, useEffect } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Row,
    Col,
    FormGroup,
    Label,
    Input,
} from "reactstrap";
import Select from "react-select";
import { ContratoStatusReportFilters } from "../../context/hooks/useContratoStatusReport";

export type TipoRelatorioContrato = "quantitativo" | "status";

export const RELATORIO_CONTRATO_STATUS_FIELDS = [
    { label: "Contrato ID", value: "ContratoId" },
    { label: "Descrição", value: "Description" },
    { label: "Turma / Classe", value: "Class" },
    { label: "Instituição", value: "Instituicao" },
    { label: "Cidade", value: "City" },
    { label: "Estado (UF)", value: "State" },
    { label: "Cursos", value: "Cursos" },
    { label: "Qtd. Contratados", value: "HiredStudents" },
    { label: "Qtd. Atuais", value: "CurrentStudents" },
    { label: "Data de Criação", value: "CreationDate" },
    { label: "Data de Conclusão", value: "CompletionDate" },
    { label: "Status Consolidado", value: "StatusConsolidado" },
    { label: "Editor", value: "Editor" },
    { label: "Laboratório", value: "Laboratorio" },
];

export const RELATORIO_CONTRATO_QUANTITATIVO_FIELDS = [
    { label: "Região", value: "Regiao" },
    { label: "Estado (UF)", value: "Estado" },
    { label: "Cidade", value: "Cidade" },
    { label: "Instituição", value: "Instituicao" },
    { label: "Nº Contratos", value: "NumerosContratos" },
    { label: "Qtd. Contratos", value: "QuantidadeContratos" },
    { label: "Qtd. Formandos", value: "QuantidadeFormandos" },
];

const REGIOES_ESTADOS: Record<string, string[]> = {
    "Norte": ["AC", "AP", "AM", "PA", "RO", "RR", "TO"],
    "Nordeste": ["AL", "BA", "CE", "MA", "PB", "PE", "PI", "RN", "SE"],
    "Centro-Oeste": ["DF", "GO", "MT", "MS"],
    "Sudeste": ["ES", "MG", "RJ", "SP"],
    "Sul": ["PR", "RS", "SC"],
};

const ESTADOS_BRASIL = [
    "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
    "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
    "RS", "RO", "RR", "SC", "SP", "SE", "TO"
];

interface Props {
    isOpen: boolean;
    toggle: () => void;
    listInstituicao?: Array<{ id: number; name: string; state?: string; city?: string }>;
    representantes?: Array<{ id: number; name: string }>;
    initialFilters?: ContratoStatusReportFilters;
    onExportPdf: (filters: ContratoStatusReportFilters, tipoRelatorio: TipoRelatorioContrato, colunas?: string[]) => void;
    onExportExcel: (filters: ContratoStatusReportFilters, tipoRelatorio: TipoRelatorioContrato, colunas?: string[]) => void;
}

export const ExportContratoRelatorioModal: React.FC<Props> = ({
    isOpen,
    toggle,
    listInstituicao = [],
    representantes = [],
    initialFilters = {},
    onExportPdf,
    onExportExcel,
}) => {
    const [tipoRelatorio, setTipoRelatorio] = useState<TipoRelatorioContrato>("quantitativo");
    const [contratoId, setContratoId] = useState<number | undefined>(initialFilters.ContratoId);
    const [instituicaoId, setInstituicaoId] = useState<number | undefined>(initialFilters.InstituicaoId);
    const [city, setCity] = useState<string>(initialFilters.City || "");
    const [state, setState] = useState<string>(initialFilters.State || "");
    const [region, setRegion] = useState<string>(initialFilters.Region || "");
    const [vendedorId, setVendedorId] = useState<number>(initialFilters.VendedorId || 0);
    const [status, setStatus] = useState<number>(initialFilters.Status || 0);
    const [startDate, setStartDate] = useState<string>(initialFilters.StartDate || "");
    const [endDate, setEndDate] = useState<string>(initialFilters.EndDate || "");
    const [somenteKit, setSomenteKit] = useState<boolean>(initialFilters.SomenteKit || false);

    const [selectedFields, setSelectedFields] = useState<string[]>([
        "Regiao",
        "Estado",
        "Cidade",
        "Instituicao",
        "NumerosContratos",
        "QuantidadeContratos",
        "QuantidadeFormandos"
    ]);

    // Lista de estados dinamicamente filtrada pela região selecionada
    const estadosDisponiveis = React.useMemo(() => {
        if (!region || !REGIOES_ESTADOS[region]) {
            return ESTADOS_BRASIL;
        }
        return REGIOES_ESTADOS[region];
    }, [region]);

    // Se o estado atual não pertencer à nova lista de estados da região, limpa o estado
    const handleRegionChange = (newRegion: string) => {
        setRegion(newRegion);
        if (newRegion && REGIOES_ESTADOS[newRegion]) {
            const estadosDaRegiao = REGIOES_ESTADOS[newRegion];
            if (state && !estadosDaRegiao.includes(state)) {
                setState("");
            }
        }
    };

    // Lista de cidades disponíveis a partir das instituições cadastradas
    const cidadesSugeridas = React.useMemo(() => {
        const cidadesSet = new Set<string>();
        listInstituicao.forEach((inst) => {
            const c = inst.city?.trim();
            const s = inst.state?.trim().toUpperCase();
            if (!c) return;

            // Se filtrou por estado, apenas as cidades desse estado
            if (state && s && s !== state.toUpperCase()) {
                return;
            }

            // Se filtrou por região e não por estado, apenas cidades dos estados da região
            if (!state && region && REGIOES_ESTADOS[region] && s) {
                if (!REGIOES_ESTADOS[region].includes(s)) {
                    return;
                }
            }

            cidadesSet.add(c);
        });

        return Array.from(cidadesSet).sort((a, b) => a.localeCompare(b, "pt-BR"));
    }, [listInstituicao, region, state]);

    useEffect(() => {
        if (isOpen) {
            setContratoId(initialFilters.ContratoId);
            setInstituicaoId(initialFilters.InstituicaoId);
            setCity(initialFilters.City || "");
            setState(initialFilters.State || "");
            setRegion(initialFilters.Region || "");
            setVendedorId(initialFilters.VendedorId || 0);
            setStatus(initialFilters.Status || 0);
            setStartDate(initialFilters.StartDate || "");
            setEndDate(initialFilters.EndDate || "");
            setSomenteKit(initialFilters.SomenteKit || false);
        }
    }, [isOpen, initialFilters]);

    useEffect(() => {
        if (tipoRelatorio === "quantitativo") {
            setSelectedFields(RELATORIO_CONTRATO_QUANTITATIVO_FIELDS.map(f => f.value));
        } else {
            setSelectedFields(["ContratoId", "Description", "Class", "Instituicao", "City", "State", "StatusConsolidado"]);
        }
    }, [tipoRelatorio]);

    const activeFieldsList = tipoRelatorio === "quantitativo"
        ? RELATORIO_CONTRATO_QUANTITATIVO_FIELDS
        : RELATORIO_CONTRATO_STATUS_FIELDS;

    const handleFieldSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fieldValue = e.target.value;
        if (!fieldValue) return;
        if (!selectedFields.includes(fieldValue)) {
            setSelectedFields([...selectedFields, fieldValue]);
        }
        e.target.value = "";
    };

    const handleRemoveField = (fieldValue: string) => {
        setSelectedFields(selectedFields.filter((f) => f !== fieldValue));
    };

    const handleSelectAllFields = () => {
        setSelectedFields(activeFieldsList.map((f) => f.value));
    };

    const handleClearAllFields = () => {
        setSelectedFields([]);
    };

    const getCompiledFilters = (): ContratoStatusReportFilters => {
        return {
            ...initialFilters,
            ContratoId: contratoId && contratoId > 0 ? contratoId : undefined,
            InstituicaoId: instituicaoId && instituicaoId > 0 ? instituicaoId : undefined,
            City: city.trim() || undefined,
            State: state.trim() || undefined,
            Region: region.trim() || undefined,
            VendedorId: vendedorId && vendedorId > 0 ? vendedorId : undefined,
            Status: status && status > 0 ? status : undefined,
            StartDate: startDate || undefined,
            EndDate: endDate || undefined,
            SomenteKit: somenteKit ? true : undefined,
        };
    };

    const handleExport = (formato: "pdf" | "excel") => {
        const compiled = getCompiledFilters();
        toggle();
        if (formato === "pdf") {
            onExportPdf(compiled, tipoRelatorio, selectedFields);
        } else {
            onExportExcel(compiled, tipoRelatorio, selectedFields);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" backdrop="static">
            <ModalHeader toggle={toggle}>
                <i className="fa-solid fa-file-contract mr-2 text-primary"></i>
                Exportar Relatório de Contratos
            </ModalHeader>
            <ModalBody>
                {/* Seleção do Tipo de Relatório */}
                <FormGroup className="mb-3">
                    <Label className="form-control-label font-weight-bold" for="modalTipoRelatorio">
                        Tipo de Relatório
                    </Label>
                    <Input
                        className="form-control-alternative font-weight-bold"
                        type="select"
                        name="modalTipoRelatorio"
                        id="modalTipoRelatorio"
                        value={tipoRelatorio}
                        onChange={(e) => setTipoRelatorio(e.target.value as TipoRelatorioContrato)}
                    >
                        <option value="quantitativo">
                            Relatório Quantitativo de Contratos (por Região / Estado / Cidade / Instituição com total de contratos e formandos)
                        </option>
                        <option value="status">
                            Relatório Detalhado de Status dos Contratos
                        </option>
                    </Input>
                    <small className="form-text text-muted mt-2" style={{ fontSize: "0.84rem", lineHeight: 1.4 }}>
                        {tipoRelatorio === "quantitativo" ? (
                            <>
                                <strong>Visão resumida/estatística:</strong> Agrupado por Região, Estado, Cidade e Instituição. Exibe a contagem de contratos e formandos por instituição/localidade e o total geral, sem detalhes de alunos ou contratos individuais.
                            </>
                        ) : (
                            <>
                                <strong>Visão detalhada/operacional:</strong> Lista contrato por contrato, com turma, instituição, cursos, datas e etapas do fluxo de produção (edição, laboratório, expedição).
                            </>
                        )}
                    </small>
                </FormGroup>

                {/* Container de Filtros Estilizado */}
                <div
                    className="p-3 mb-3"
                    style={{
                        backgroundColor: "#f8f9fe",
                        borderRadius: 8,
                        border: "1px solid #e9ecef",
                    }}
                >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="text-primary mb-0">Filtros do Relatório</h5>
                        <FormGroup check className="mb-0">
                            <Label check className="font-weight-normal" style={{ fontSize: "0.88rem" }}>
                                <Input
                                    type="checkbox"
                                    checked={somenteKit}
                                    onChange={(e) => setSomenteKit(e.target.checked)}
                                />
                                Somente Kit
                            </Label>
                        </FormGroup>
                    </div>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Instituição</Label>
                                <Select
                                    placeholder="Todas ou selecione..."
                                    isClearable
                                    options={listInstituicao.map((i) => ({ value: i.id, label: i.name }))}
                                    value={
                                        instituicaoId
                                            ? {
                                                  value: instituicaoId,
                                                  label:
                                                      listInstituicao.find((i) => i.id === instituicaoId)?.name ||
                                                      `Instituição #${instituicaoId}`,
                                              }
                                            : null
                                    }
                                    onChange={(opt) => setInstituicaoId(opt ? (opt as any).value : undefined)}
                                    noOptionsMessage={() => "Nenhuma instituição encontrada"}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Contrato específico</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="number"
                                    placeholder="Todos ou digite o nº (ex: 123)"
                                    value={contratoId || ""}
                                    onChange={(e) =>
                                        setContratoId(e.target.value ? Number(e.target.value) : undefined)
                                    }
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Região</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="select"
                                    value={region}
                                    onChange={(e) => handleRegionChange(e.target.value)}
                                >
                                    <option value="">Todas</option>
                                    <option value="Norte">Norte</option>
                                    <option value="Nordeste">Nordeste</option>
                                    <option value="Centro-Oeste">Centro-Oeste</option>
                                    <option value="Sudeste">Sudeste</option>
                                    <option value="Sul">Sul</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Estado (UF)</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="select"
                                    value={state}
                                    onChange={(e) => setState(e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    {estadosDisponiveis.map((uf) => (
                                        <option key={uf} value={uf}>
                                            {uf}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Cidade</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    list="sugestoes-cidades-relatorio"
                                    placeholder="Filtrar ou selecione a cidade..."
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    autoComplete="off"
                                />
                                <datalist id="sugestoes-cidades-relatorio">
                                    {cidadesSugeridas.map((nomeCidade) => (
                                        <option key={nomeCidade} value={nomeCidade} />
                                    ))}
                                </datalist>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Representante Comercial</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="select"
                                    value={vendedorId}
                                    onChange={(e) => setVendedorId(Number(e.target.value))}
                                >
                                    <option value={0}>Todos</option>
                                    {representantes.map((rep) => (
                                        <option key={rep.id} value={rep.id}>
                                            {rep.name}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Status do Contrato</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="select"
                                    value={status}
                                    onChange={(e) => setStatus(Number(e.target.value))}
                                >
                                    <option value={0}>Todos</option>
                                    <option value={1}>Aberto</option>
                                    <option value={2}>Em andamento</option>
                                    <option value={3}>Finalizado</option>
                                    <option value={4}>Expedição</option>
                                    <option value={5}>Venda</option>
                                    <option value={6}>Cancelado</option>
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Data Inicial</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Data Final</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </div>

                {/* Seleção de Colunas */}
                <FormGroup className="mb-0">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <Label className="form-control-label font-weight-bold mb-0">
                            Colunas do Relatório ({selectedFields.length} selecionadas)
                        </Label>
                        <div>
                            <Button size="sm" color="link" onClick={handleSelectAllFields} className="p-0 mr-3">
                                Selecionar todas
                            </Button>
                            <Button size="sm" color="link" onClick={handleClearAllFields} className="p-0 text-danger">
                                Limpar seleção
                            </Button>
                        </div>
                    </div>

                    <Input
                        className="form-control-alternative mb-2"
                        type="select"
                        name="field"
                        onChange={handleFieldSelection}
                        defaultValue=""
                    >
                        <option value="">Adicionar campo...</option>
                        {activeFieldsList
                            .filter((f) => !selectedFields.includes(f.value))
                            .map((field) => (
                                <option key={field.value} value={field.value}>
                                    + {field.label}
                                </option>
                            ))}
                    </Input>

                    <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                        {selectedFields.map((value) => {
                            const field = activeFieldsList.find((f) => f.value === value);
                            return (
                                <span
                                    key={value}
                                    className="badge badge-primary d-inline-flex align-items-center py-2 px-2"
                                    style={{ fontSize: "0.85rem" }}
                                >
                                    {field?.label || value}
                                    <button
                                        type="button"
                                        className="close ml-2 text-white"
                                        style={{ outline: "none", fontSize: "1rem", lineHeight: 1 }}
                                        onClick={() => handleRemoveField(value)}
                                    >
                                        &times;
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                </FormGroup>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" type="button" onClick={toggle}>
                    Cancelar
                </Button>
                <Button
                    style={{ color: "#FFF", backgroundColor: "#2dce89" }}
                    type="button"
                    onClick={() => handleExport("excel")}
                    disabled={selectedFields.length === 0}
                >
                    <i className="fa-solid fa-file-excel mr-2"></i>
                    Excel
                </Button>
                <Button
                    color="danger"
                    type="button"
                    onClick={() => handleExport("pdf")}
                    disabled={selectedFields.length === 0}
                >
                    <i className="fa-solid fa-file-pdf mr-2"></i>
                    PDF
                </Button>
            </ModalFooter>
        </Modal>
    );
};
